import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Auth methods
  initializeAuth: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  isLoading: false,
  error: null,

  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  initializeAuth: async () => {
    try {
      set({ isLoading: true, error: null });

      // Check for existing session
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw error;

      if (session) {
        // Validate the session is still valid by checking with the server
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError || !user) {
          // Session is stale/invalid — sign out and clear state
          console.warn('[AuthStore] Stale session detected, signing out');
          await supabase.auth.signOut();
          set({ session: null, user: null, isLoading: false });
        } else {
          set({
            session,
            user: session.user,
            isLoading: false
          });
        }
      } else {
        set({
          session: null,
          user: null,
          isLoading: false
        });
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange((_event: string, newSession: { user: User | null } | null) => {
        console.log('[AuthStore] Auth state changed');
        set({
          session: newSession as Session | null,
          user: newSession?.user ?? null,
          isLoading: false
        });
      });
    } catch (error) {
      // If auth initialization fails, clear session to force login
      await supabase.auth.signOut().catch(() => {});
      set({
        session: null,
        user: null,
        error: error instanceof Error ? error.message : 'Auth initialization failed',
        isLoading: false
      });
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Google sign in failed',
        isLoading: false
      });
    }
  },

  signInWithEmail: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Handle specific error messages
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please try again.');
        }
        if (error.message.includes('Email not confirmed')) {
          throw new Error('Please verify your email address before signing in.');
        }
        throw error;
      }

      if (data.session) {
        set({
          session: data.session,
          user: data.user,
          isLoading: false
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Sign in failed',
        isLoading: false
      });
    }
  },

  signUpWithEmail: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        if (error.message.includes('User already registered')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        throw error;
      }

      // Check if email confirmation is required
      if (data.user && !data.session) {
        set({
          isLoading: false,
          error: null
        });
        throw new Error('Please check your email to verify your account before signing in.');
      }

      if (data.session) {
        set({
          session: data.session,
          user: data.user,
          isLoading: false
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Sign up failed',
        isLoading: false
      });
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({ user: null, session: null, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Sign out failed',
        isLoading: false
      });
    }
  },

  resetPassword: async (email: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      if (error) {
        if (error.message.includes('User not found')) {
          // Don't reveal if user exists for security
          // Just return success silently
          set({ isLoading: false });
          return;
        }
        throw error;
      }
      
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Password reset failed',
        isLoading: false
      });
    }
  },

  updatePassword: async (newPassword: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Password update failed',
        isLoading: false
      });
    }
  },
}));
